import type {
  RemoteError,
  ShellHeaderActions,
} from '@wandb/forge-shell-contract';
import {createContext, useContext} from 'react';

declare global {
  interface Window {
    __FORGE_SHELL_CONFIG__?: unknown;
  }
}

let shellErrorHandler: ((error: RemoteError) => void) | undefined;

export const ActiveProductIdContext = createContext<string | undefined>(
  undefined
);

export const useActiveProductId = (): string | undefined =>
  useContext(ActiveProductIdContext);

export const registerShellErrorHandler = (
  handler: (error: RemoteError) => void
): (() => void) => {
  const previousHandler = shellErrorHandler;
  shellErrorHandler = handler;
  return () => {
    shellErrorHandler = previousHandler;
  };
};

type Opener = () => void;
type ShellOpenerKey = 'globalSearch' | 'keyboardShortcuts';

const openers: Partial<Record<ShellOpenerKey, Opener>> = {};
const headerActionListeners = new Set<() => void>();

const notifyHeaderActions = (): void => {
  for (const listener of headerActionListeners) {
    listener();
  }
};

/**
 * Lets the mounted app hand the shell a way to open one of its features. The
 * shell's header lives outside our React root, so an opener is registered from
 * inside the tree that owns the feature (and only while that feature is
 * mounted, e.g. behind its ramp flag). The mount publishes the current set to
 * the shell whenever it changes so it can show or hide the matching control.
 */
const registerShellOpener = (
  key: ShellOpenerKey,
  open: Opener
): (() => void) => {
  const previousOpener = openers[key];
  openers[key] = open;
  notifyHeaderActions();
  return () => {
    if (openers[key] === open) {
      openers[key] = previousOpener;
      notifyHeaderActions();
    }
  };
};

export const registerGlobalSearchOpener = (open: Opener): (() => void) =>
  registerShellOpener('globalSearch', open);

export const registerKeyboardShortcutsOpener = (open: Opener): (() => void) =>
  registerShellOpener('keyboardShortcuts', open);

export const shellHeaderActions = (): ShellHeaderActions => ({
  onGlobalSearchPress: openers.globalSearch,
  onKeyboardShortcutsPress: openers.keyboardShortcuts,
});

export const subscribeShellHeaderActions = (
  listener: () => void
): (() => void) => {
  headerActionListeners.add(listener);
  return () => {
    headerActionListeners.delete(listener);
  };
};

export const isMountedInShell = (): boolean =>
  typeof window !== 'undefined' && window.__FORGE_SHELL_CONFIG__ !== undefined;

const shellIdentityOrigin = (): string | undefined => {
  const config = window.__FORGE_SHELL_CONFIG__;
  if (typeof config !== 'object' || config === null) {
    return undefined;
  }
  const {origins} = config as {origins?: unknown};
  if (typeof origins !== 'object' || origins === null) {
    return undefined;
  }
  const {identity} = origins as {identity?: unknown};
  return typeof identity === 'string' && identity !== '' ? identity : undefined;
};

/**
 * Login URL on the shell's identity surface, which owns authentication for every
 * surface the shell serves. `next` is the shell path to return to afterwards.
 * Undefined when the app runs standalone, or the shell publishes no identity
 * origin, so callers fall back to the app's own login flow.
 */
export const shellIdentityLoginUrl = (next?: string): string | undefined =>
  shellIdentityUrl('/login', next);

/**
 * Sign-up on the shell's identity surface, undefined under the same conditions
 * as shellIdentityLoginUrl. It takes no `next`: a completed sign-up continues
 * into onboarding rather than back to the page the user came from.
 */
export const shellIdentitySignupUrl = (): string | undefined =>
  shellIdentityUrl('/signup');

const shellIdentityUrl = (path: string, next?: string): string | undefined => {
  const origin = shellIdentityOrigin();
  if (origin === undefined) {
    return undefined;
  }

  try {
    const url = new URL(path, origin);
    if (next) {
      url.searchParams.set('next', next);
    }
    return url.toString();
  } catch {
    return undefined;
  }
};

export const hasShellErrorHandler = (): boolean => Boolean(shellErrorHandler);

export const reportUnauthenticatedToShell = (): boolean => {
  if (!shellErrorHandler) {
    return false;
  }

  const error: RemoteError = {
    kind: 'unauthenticated',
    message: 'This remote requires an authenticated viewer.',
  };
  shellErrorHandler(error);
  return true;
};
